import { ClientConfig } from 'client/configuration';
import { isNode } from 'client/utils/environment';
import { setCookie } from 'client/utils/cookie/helpers/set-cookie';
import { logger } from 'client/utils/logger/logger';

const EDM_IAS_DATA_COOKIE_NAME = 'edm-ias-data';
const EDM_IAS_DATA_TIMEOUT = 2000;

// IAS Content Classification Safeguarding keyword mappings
// ADS-12404: Map obfuscated IAS keywords to fr flag
const IAS_IVT_KEYWORDS = ['IAS_458326452_PG'];
const IAS_CLEAN_KEYWORDS = [
  'IAS_130586472_PG',
  'IAS_562904284_PG',
  'IAS_219298679_PG',
  'IAS_328621465_PG',
  'IAS_959173900_PG',
  'IAS_552567669_PG',
  'IAS_538151164_PG',
  'IAS_657804325_PG',
  'IAS_124687610_PG',
  'IAS_579741992_PG',
];

function setIASDataCookie(data) {
  const newCookieValue = JSON.stringify({
    fr: data.fr,
    // Store the specific keyword that determined the fr value (for AdOps tracking)
    matchedKeyword: data.matchedKeyword || null,
    // Store original keywords for debugging/monitoring
    iasKeywords: data.custom?.['ias-kw'] || [],
  });
  document.cookie = setCookie(EDM_IAS_DATA_COOKIE_NAME, newCookieValue, { path: '/' }, true);
}

/**
 * Maps IAS custom keywords to fr flag for Content Classification Safeguarding
 * ADS-12404: Handle obfuscated IAS keyword mappings
 *
 * Rules:
 * - If IAS returns no keywords, no fr determination can be made => { fr: null }
 * - If any keyword is in IAS_IVT_KEYWORDS => fr=true
 * - Otherwise (any non-empty keywords and none IVT) => fr=false
 *
 * @returns {Object} { fr: 'true'|'false'|null, matchedKeyword: string|null }
 */
function mapIasCustomToFr(customData) {
  const iasKeywords = customData?.['ias-kw'] || [];

  // If IAS returned no keywords, we cannot make any fr determination.
  if (iasKeywords.length === 0) {
    return { fr: null, matchedKeyword: null };
  }

  // Check for IVT first (higher priority)
  const ivtKeyword = iasKeywords.find(keyword => IAS_IVT_KEYWORDS.includes(keyword));
  if (ivtKeyword) {
    return { fr: 'true', matchedKeyword: ivtKeyword };
  }

  // Check for clean traffic
  const cleanKeyword = iasKeywords.find(keyword => IAS_CLEAN_KEYWORDS.includes(keyword));
  if (cleanKeyword) {
    return { fr: 'false', matchedKeyword: cleanKeyword };
  }

  // Unknown keywords — cannot classify, leave fr as null
  return { fr: null, matchedKeyword: null };
}
export function getStoredIasData(iasData) {
  try {
    return JSON.parse(iasData);
  } catch {
    return null;
  }
}

/**
 * requires modern Grade A browser that supports AbortController
 */
export async function fetchWithTimeout(url) {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), EDM_IAS_DATA_TIMEOUT);

  let response;
  try {
    response = await fetch(url, {
      signal: controller.signal,
      credentials: 'omit',
    });
  } finally {
    clearTimeout(id);
  }

  if (!response) {
    logger.warn('IAS fetch returned no response object');
    return null;
  }

  const contentType = response.headers?.get('content-type') || '';

  // IAS can return non-JSON (HTML challenge page), redirects, or empty bodies.
  // Avoid throwing JSON parse errors that mask the real failure mode.
  if (!contentType.includes('application/json')) {
    logger.warn(`IAS returned non-JSON response: status=${response.status}, content-type=${contentType}`);
    return null;
  }

  try {
    return await response.json();
  } catch (error) {
    logger.warn(error, `IAS returned invalid JSON: status=${response.status}, content-type=${contentType}`);
    return null;
  }
}

/**
 * makes a request to IAS pub service that returns user data
 * purpose of this function is to retrieve IAS fr flag to be used in ad targeting
 */
export async function getIasData(session, iasData) {
  if (isNode()) {
    return null; // disabled on server side
  }

  if (!session) {
    return null;
  }

  const iasDataValue = getStoredIasData(iasData);
  if (iasDataValue !== null) {
    return iasDataValue;
  }

  const windowWidth = window.innerWidth;
  const screenWidth = window.screen.width;
  const windowUrl = encodeURI(window.location.href);
  const url = ClientConfig.get('iasApiUrl')
    .replace('{windowWidth}', windowWidth)
    .replace('{screenWidth}', screenWidth)
    .replace('{windowUrl}', windowUrl)
    .replace('{visitorSession}', session);

  try {
    const responseData = await fetchWithTimeout(url);

    if (!responseData) {
      logger.warn('IAS returned no usable response payload (blocked/non-JSON/invalid JSON)');
      return null;
    }

    const mappedResult = mapIasCustomToFr(responseData.custom);

    const iasKw = responseData.custom?.['ias-kw'] || [];

    if (iasKw.length === 0) {
      logger.warn('IAS returned empty ias-kw array; no fr determination made');
    } else {
      const IVT_SET = new Set(IAS_IVT_KEYWORDS);
      const CLEAN_SET = new Set(IAS_CLEAN_KEYWORDS);

      const hasKnownKeyword = iasKw.some(keyword => IVT_SET.has(keyword) || CLEAN_SET.has(keyword));

      logger.info(
        `IAS mapping applied: ${iasKw} -> fr=${mappedResult.fr}, matchedKeyword=${mappedResult.matchedKeyword}`
      );

      if (!hasKnownKeyword) {
        logger.warn(`IAS returned unrecognized ias-kw values; no fr determination made: ${iasKw}`);
      }
    }

    // If IAS returned no known keywords, do not set fr and do not write a cookie.
    if (mappedResult.fr === null) {
      return null;
    }

    const processedData = {
      ...responseData,
      fr: mappedResult.fr,
      matchedKeyword: mappedResult.matchedKeyword,
    };

    // Save and return IAS data when fr is defined (both 'true' and 'false' strings are valid)
    if (processedData.fr === 'true' || processedData.fr === 'false') {
      setIASDataCookie(processedData);
      return processedData;
    }
  } catch (error) {
    const msg = error?.message || '';

    // Expected noise (ad/pixel domain blocked, offline, aborted) — keep out of error logs.
    if (
      error?.name === 'AbortError' ||
      msg.includes('Failed to fetch') ||
      msg.includes('NetworkError') ||
      msg.includes('The Internet connection appears to be offline')
    ) {
      logger.warn(error, 'IAS fetch failed (blocked/offline/timeout)');
      return null;
    }

    logger.error(error, 'Error while fetching getIasData');
  }
  return null;
}

export function getIvtClassName(iasData = null, isEnableIAS) {
  let isIvt = 'false';
  if (iasData !== null) {
    const iasDataValue = isEnableIAS && getStoredIasData(iasData);
    isIvt = iasDataValue?.fr ?? null;
  }
  let ivtClassName;
  if (isIvt === 'true') {
    ivtClassName = 'invisible is-ivt';
  }
  return ivtClassName;
}
